import { Link, useLocation } from 'react-router-dom';
import type { SharedSpace } from '../../lib/db';
import { SPACE_TEMPLATES } from '../../config/space-templates';

interface SpaceNavItemProps {
  space: SharedSpace;
}

export const SpaceNavItem = ({ space }: SpaceNavItemProps) => {
  const location = useLocation();
  const template = SPACE_TEMPLATES[space.type];

  // Determine if this space is a notes-type space (uses NotesPage UI)
  const isNotesType = space.type === 'notes';

  // Determine if this space is currently active
  // Check both path-based routes and query-param based routes
  const searchParams = new URLSearchParams(location.search);
  const currentSpaceId = searchParams.get('id') || searchParams.get('spaceId');
  const isActive = location.pathname.includes(`/spaces/${space.id}`) ||
                   (location.pathname === '/spaces' && currentSpaceId === space.id) ||
                   (location.pathname === '/notes' && currentSpaceId === space.id);

  // Route notes-type spaces to /notes, others to /spaces
  const linkTo = isNotesType ? `/notes?spaceId=${space.id}` : `/spaces?id=${space.id}`;

  // Get icon and description from template, with fallbacks
  const icon = template?.icon || '📄';
  const typeLabel = template?.description || space.type;

  // Check if space is shared (has members other than just the owner)
  const isShared = space.members && space.members.length > 0;
  const memberCount = space.members?.length || 0;

  return (
    <Link
      to={linkTo}
      className={`block px-3 py-2 rounded-lg transition-colors ${
        isActive
          ? 'bg-sky-blue dark:bg-sky-blue-600 text-white'
          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
      }`}
    >
      <div className="flex items-start gap-2">
        {/* Icon */}
        <span className="text-lg flex-shrink-0 mt-0.5">{icon}</span>

        <div className="flex-1 min-w-0">
          {/* Space Name */}
          <div className="flex items-center gap-1.5">
            <span className={`text-sm font-medium truncate ${
              isActive ? 'text-white' : 'text-gray-900 dark:text-white'
            }`}>
              {space.name}
            </span>
            {/* Shared Badge - Only show if space has members */}
            {isShared && (
              <span
                className="flex-shrink-0 text-xs"
                title={`Shared with ${memberCount} ${memberCount === 1 ? 'person' : 'people'}`}
              >
                👥
              </span>
            )}
          </div>

          {/* Space Type/Description */}
          <div className={`text-xs truncate ${
            isActive ? 'text-white/75' : 'text-gray-500 dark:text-gray-400'
          }`}>
            {typeLabel}
            {isShared && ` • ${memberCount} ${memberCount === 1 ? 'member' : 'members'}`}
          </div>
        </div>
      </div>
    </Link>
  );
};
